/**
 * IndexedDB 缓存工具
 * 为 OCR 请求提供前端缓存机制，支持 TTL、缓存大小限制和条件缓存
 */

// 默认配置
const DEFAULT_CONFIG = {
  dbName: 'ocr_cache_db',
  storeName: 'cache_store',
  version: 1,
  ttl: 24 * 60 * 60 * 1000, // 24小时，单位毫秒
  maxItems: 1000, // 最大缓存条目数
  enableCache: true, // 是否启用缓存
  skipCacheCmds: ['BatchCheckWhitelist', 'AddUserInterfaceHistoryAccessRecordForDemo', 'QueryUserInterfaceHistoryAccessRecordForDemo', 'GetProductConfig'], // 不缓存的接口命令列表
};

export { DEFAULT_CONFIG };

// 全局数据库实例缓存
let dbInstance = null;
let dbPromise = null;

/**
 * 稳定的 JSON 字符串化，确保对象键的顺序一致
 * @param {any} obj - 要序列化的对象
 * @returns {string} 稳定序列化的字符串
 */
export function stableStringify(obj) {
  if (obj === null || obj === undefined) {
    return String(obj);
  }
  if (typeof obj !== 'object') {
    return JSON.stringify(obj);
  }
  if (Array.isArray(obj)) {
    const items = obj.map(item => stableStringify(item));
    return `[${items.join(',')}]`;
  }
  // 对象：按键排序
  const sortedKeys = Object.keys(obj).sort();
  const items = sortedKeys.map(key => {
    const keyStr = JSON.stringify(key);
    const valueStr = stableStringify(obj[key]);
    return `${keyStr}:${valueStr}`;
  });
  return `{${items.join(',')}}`;
}

/**
 * 生成缓存键：基于请求配置的稳定哈希
 * @param {Object} requestConfig - baseOcrRequest 的配置对象
 * @returns {string} 缓存键
 */
export function generateCacheKey(requestConfig) {
  return stableStringify(requestConfig);
}

/**
 * 打开 IndexedDB 数据库
 * @param {Object} config - 配置对象
 * @returns {Promise<IDBDatabase>} 数据库实例
 */
export function openDatabase(config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  
  if (dbPromise) {
    return dbPromise;
  }
  
  dbPromise = new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }
    
    const request = indexedDB.open(mergedConfig.dbName, mergedConfig.version);
    
    request.onerror = () => {
      reject(new Error(`打开数据库失败: ${request.error}`));
    };
    
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(mergedConfig.storeName)) {
        const store = db.createObjectStore(mergedConfig.storeName, { keyPath: 'key' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('expiry', 'expiry', { unique: false });
      }
    };
  });
  
  return dbPromise;
}

/**
 * 关闭数据库连接
 */
export function closeDatabase() {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
    dbPromise = null;
  }
}

/**
 * 存储缓存条目
 * @param {string} key - 缓存键
 * @param {Object} request - 请求配置
 * @param {Object} response - 响应数据
 * @param {Object} config - 配置对象
 * @param {number} [timestamp] - 可选的时间戳（毫秒），用于记录最后获取结果的时间，默认为当前时间
 * @returns {Promise<void>}
 */
export async function storeCache(key, request, response, config = {}, timestamp = null) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const db = await openDatabase(mergedConfig);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([mergedConfig.storeName], 'readwrite');
    const store = transaction.objectStore(mergedConfig.storeName);
    
    // 使用传入的时间戳，或者默认使用当前时间
    const now = timestamp && typeof timestamp === 'number' ? timestamp : Date.now();
    // 确保 ttl 是有效的正数
    const rawTtl = mergedConfig.ttl;
    const ttl = typeof rawTtl === 'number' && !isNaN(rawTtl) && rawTtl > 0 
      ? rawTtl 
      : DEFAULT_CONFIG.ttl;
    
    const entry = {
      key,
      request,
      response,
      timestamp: now,
      expiry: now + ttl,
    };
    
    const requestStore = store.put(entry);
    
    requestStore.onerror = () => {
      reject(new Error(`存储缓存失败: ${requestStore.error}`));
    };
    
    requestStore.onsuccess = () => {
      resolve();
    };
  });
}

/**
 * 获取缓存条目，如果过期则返回 null
 * @param {string} key - 缓存键
 * @param {Object} config - 配置对象
 * @returns {Promise<Object|null>} 缓存条目或 null
 */
export async function getCache(key, config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const db = await openDatabase(mergedConfig);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([mergedConfig.storeName], 'readonly');
    const store = transaction.objectStore(mergedConfig.storeName);
    
    const request = store.get(key);
    
    request.onerror = () => {
      reject(new Error(`获取缓存失败: ${request.error}`));
    };
    
    request.onsuccess = () => {
      const entry = request.result;
      if (!entry) {
        resolve(null);
        return;
      }
      
      const now = Date.now();
      const expiry = entry.expiry;
      
      // 检查 expiry 是否有效（应为数字且大于0）
      if (typeof expiry !== 'number' || isNaN(expiry) || expiry <= 0 || now > expiry) {
        // 缓存过期或 expiry 无效，异步删除并返回 null
        deleteCache(key, mergedConfig).catch(error => {
          console.error('[Cache] 删除过期缓存失败:', error);
        });
        resolve(null);
        return;
      }
      
      resolve(entry);
    };
  });
}

/**
 * 删除缓存条目
 * @param {string} key - 缓存键
 * @param {Object} config - 配置对象
 * @returns {Promise<void>}
 */
export async function deleteCache(key, config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const db = await openDatabase(mergedConfig);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([mergedConfig.storeName], 'readwrite');
    const store = transaction.objectStore(mergedConfig.storeName);
    
    const request = store.delete(key);
    
    request.onerror = () => {
      reject(new Error(`删除缓存失败: ${request.error}`));
    };
    
    request.onsuccess = () => {
      resolve();
    };
  });
}

/**
 * 清理过期缓存条目
 * @param {Object} config - 配置对象
 * @returns {Promise<number>} 删除的条目数量
 */
export async function clearExpired(config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const db = await openDatabase(mergedConfig);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([mergedConfig.storeName], 'readwrite');
    const store = transaction.objectStore(mergedConfig.storeName);
    const expiryIndex = store.index('expiry');
    
    const now = Date.now();
    const range = IDBKeyRange.upperBound(now);
    const request = expiryIndex.openCursor(range);
    
    let deleteCount = 0;
    
    request.onerror = () => {
      reject(new Error(`清理过期缓存失败: ${request.error}`));
    };
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        deleteCount++;
        cursor.continue();
      } else {
        resolve(deleteCount);
      }
    };
  });
}

/**
 * 获取当前缓存条目数量
 * @param {Object} config - 配置对象
 * @returns {Promise<number>} 条目数量
 */
export async function getCacheCount(config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const db = await openDatabase(mergedConfig);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([mergedConfig.storeName], 'readonly');
    const store = transaction.objectStore(mergedConfig.storeName);
    
    const request = store.count();
    
    request.onerror = () => {
      reject(new Error(`获取缓存数量失败: ${request.error}`));
    };
    
    request.onsuccess = () => {
      resolve(request.result);
    };
  });
}

/**
 * 修剪缓存，确保不超过最大条目数
 * 删除最旧的条目（基于时间戳）
 * @param {Object} config - 配置对象
 * @returns {Promise<number>} 删除的条目数量
 */
export async function trimCache(config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const db = await openDatabase(mergedConfig);
  
  const currentCount = await getCacheCount(mergedConfig);
  if (currentCount <= mergedConfig.maxItems) {
    return 0;
  }
  
  const itemsToDelete = currentCount - mergedConfig.maxItems;
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([mergedConfig.storeName], 'readwrite');
    const store = transaction.objectStore(mergedConfig.storeName);
    const timestampIndex = store.index('timestamp');
    
    const request = timestampIndex.openCursor();
    let deleteCount = 0;
    
    request.onerror = () => {
      reject(new Error(`修剪缓存失败: ${request.error}`));
    };
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor && deleteCount < itemsToDelete) {
        cursor.delete();
        deleteCount++;
        cursor.continue();
      } else {
        resolve(deleteCount);
      }
    };
  });
}

/**
 * 清空所有缓存
 * @param {Object} config - 配置对象
 * @returns {Promise<void>}
 */
export async function clearAllCache(config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const db = await openDatabase(mergedConfig);
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([mergedConfig.storeName], 'readwrite');
    const store = transaction.objectStore(mergedConfig.storeName);
    
    const request = store.clear();
    
    request.onerror = () => {
      reject(new Error(`清空缓存失败: ${request.error}`));
    };
    
    request.onsuccess = () => {
      resolve();
    };
  });
}

export default {
  stableStringify,
  generateCacheKey,
  openDatabase,
  closeDatabase,
  storeCache,
  getCache,
  deleteCache,
  clearExpired,
  getCacheCount,
  trimCache,
  clearAllCache,
  DEFAULT_CONFIG,
};